import { useQuery } from '@apollo/client/react';
import { useMutation } from '@tanstack/react-query';
import { format, formatISO, parseISO } from 'date-fns';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { useTranslation } from 'react-i18next';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { SidebarLayoutHeader } from '~/layout/components/sidebar-layout-header/SidebarLayoutHeader';
import { StandardHeader } from '~/layout/components/standard-header/StandardHeader';
import { View } from 'react-native';
import { WorkoutHeaderActions } from '~/workout/components/workout-header-actions/WorkoutHeaderActions';
import { WorkoutHeaderMenu } from '~/workout/components/workout-header-menu/WorkoutHeaderMenu';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import type { WorkoutRecord } from '~/powersync/generated/schema';

export interface WorkoutBuilderHeaderProps {
  title?: string;
  workout?: WorkoutRecord | null;
}

export interface WorkoutBuilderHeaderBodyProps {
  className?: string;
  title?: string;
  workout?: WorkoutRecord | null;
}

const WorkoutBuilderHeaderBody = ({
  className,
  title,
  workout,
}: WorkoutBuilderHeaderBodyProps) => {
  const { t } = useTranslation('workout');
  const router = useRouter();
  const powerSync = usePowerSync();
  const { initialDate } = useGlobalSearchParams<{ initialDate?: string }>();
  const { data } = useQuery(CurrentUserDocument);
  const firstName = data?.currentUser?.firstName ?? '';
  const today = new Date();

  // The header date picker edits the planned date of the workout being
  // built. For an existing workout it persists straight to the record; in
  // create mode (no workout yet) it drives the create form via the
  // `initialDate` route param.
  const updatePlannedAtMutation = useMutation({
    mutationFn: (plannedAt: Date) => {
      if (!workout) return;
      return db.Workout.mutations.update(powerSync, workout.id, {
        plannedAt: formatISO(plannedAt, { representation: 'date' }),
      });
    },
  });

  const plannedAt = workout
    ? workout.plannedAt
      ? parseISO(workout.plannedAt.slice(0, 10))
      : today
    : initialDate
      ? parseISO(initialDate.slice(0, 10))
      : today;

  const handleDateChange = (newDate: Date) => {
    if (workout) {
      updatePlannedAtMutation.mutate(newDate);
    } else {
      router.setParams({ initialDate: format(newDate, 'yyyy-MM-dd') });
    }
  };

  const isWorkoutInProgress =
    workout &&
    WorkoutUtils.getCompletionStatus(workout) ===
      WorkoutCompletionStatus.STARTED;

  return (
    <StandardHeader
      className={className}
      subtitle={t('builder.header.label', { defaultValue: 'Workout' })}
      title={title || firstName}
    >
      {isWorkoutInProgress ? (
        <WorkoutHeaderActions workout={workout} />
      ) : (
        <View className="flex-row items-center gap-2">
          <StandardHeader.Date
            currentDate={plannedAt}
            onDateChange={handleDateChange}
          />
          {workout ? <WorkoutHeaderMenu workout={workout} /> : null}
        </View>
      )}
    </StandardHeader>
  );
};

export const WorkoutBuilderHeader = Object.assign(
  ({ title, workout }: WorkoutBuilderHeaderProps) => {
    return (
      <SidebarLayoutHeader>
        <WorkoutBuilderHeaderBody title={title} workout={workout} />
      </SidebarLayoutHeader>
    );
  },
  { Body: WorkoutBuilderHeaderBody },
);
